'use client';

import type { AutoFixLoopApiSource } from '@/lib/auto-fix-loop/analytics';
import type { DispatchAutoFixLoopIterationResult } from '@/lib/auto-fix-loop/dispatch';
import type { AutoFixLoopStatusView } from '@/lib/auto-fix-loop/status';
import { queryCollectionOptions } from '@tanstack/query-db-collection';
import type { Collection } from '@tanstack/react-db';
import { createCollection } from '@tanstack/react-db';
import type { QueryClient } from '@tanstack/react-query';

/** Single-row collection: the status view for one pull request. */
export const AUTO_FIX_LOOP_ROW_KEY = 'status';

export type AutoFixLoopCollection = Collection<AutoFixLoopStatusView, string>;

export function autoFixLoopStatusQueryKey(owner: string, repo: string, number: string) {
  return ['auto-fix-loop', owner, repo, number] as const;
}

/** The loop's control API for one pull request: GET reads the status, POST toggles or retries. */
export function autoFixLoopApiUrl(owner: string, repo: string, number: string) {
  return `/api/auto-fix-loop/pr/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/${encodeURIComponent(number)}`;
}

/**
 * What the control API's POST answers once it took the request: the loop's fresh status, with
 * `eligibility` left null, whether the request changed it, and the dispatch it started, if any.
 */
export type AutoFixLoopActionResponse = AutoFixLoopStatusView & {
  changed: boolean;
  dispatch: DispatchAutoFixLoopIterationResult | null;
};

/**
 * The control API's answer, or its refusal: its own words and code when it gave any, both null
 * when the request could not be made.
 */
export type AutoFixLoopActionResult =
  | { ok: true; data: AutoFixLoopActionResponse }
  | { ok: false; error: string | null; code: string | null };

/**
 * Turns the loop on or off, or retries its failed run, through the control API. Resolves to the
 * refusal instead of throwing.
 */
export async function postAutoFixLoopAction(
  owner: string,
  repo: string,
  number: string,
  body: { action: 'enable' | 'disable' | 'retry'; source: AutoFixLoopApiSource },
): Promise<AutoFixLoopActionResult> {
  let response: Response;
  try {
    response = await fetch(autoFixLoopApiUrl(owner, repo, number), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, error: null, code: null };
  }
  const payload: unknown = await response.json().catch(() => null);
  if (response.ok && payload !== null) {
    return { ok: true, data: payload as AutoFixLoopActionResponse };
  }
  const refusal = typeof payload === 'object' && payload !== null ? payload : {};
  return {
    ok: false,
    error: 'error' in refusal && typeof refusal.error === 'string' ? refusal.error : null,
    code: 'code' in refusal && typeof refusal.code === 'string' ? refusal.code : null,
  };
}

async function fetchAutoFixLoopStatus(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<AutoFixLoopStatusView> {
  const response = await fetch(autoFixLoopApiUrl(owner, repo, number), { signal });
  if (!response.ok) {
    throw new Error('Failed to load auto-fix loop status');
  }
  return (await response.json()) as AutoFixLoopStatusView;
}

const autoFixLoopCollectionsByQueryClient = new WeakMap<
  QueryClient,
  Map<string, AutoFixLoopCollection>
>();

// Same casing as the query key so one collection maps to exactly one query.
function getAutoFixLoopCollectionCacheKey(owner: string, repo: string, number: string) {
  return `${owner}/${repo}#${number}`;
}

export function getAutoFixLoopCollection(
  queryClient: QueryClient,
  owner: string,
  repo: string,
  number: string,
): AutoFixLoopCollection {
  let collections = autoFixLoopCollectionsByQueryClient.get(queryClient);

  if (!collections) {
    collections = new Map();
    autoFixLoopCollectionsByQueryClient.set(queryClient, collections);
  }

  const cacheKey = getAutoFixLoopCollectionCacheKey(owner, repo, number);
  const existingCollection = collections.get(cacheKey);
  if (existingCollection) {
    return existingCollection;
  }

  const collection = createCollection(
    queryCollectionOptions({
      id: `auto-fix-loop:${cacheKey}`,
      queryKey: autoFixLoopStatusQueryKey(owner, repo, number),
      queryFn: ({ signal }) => fetchAutoFixLoopStatus(owner, repo, number, signal),
      select: (view: AutoFixLoopStatusView) => [view],
      getKey: () => AUTO_FIX_LOOP_ROW_KEY,
      queryClient,
    }),
  ) as AutoFixLoopCollection;

  collections.set(cacheKey, collection);
  return collection;
}
